import { BaseFiniteScrollCarousel } from "@faire/deprecated-retailer-visitor-shared/components/Carousel/FiniteScrollCarousel/BaseFiniteScrollCarousel";
import {
  Pill,
  PillProps,
} from "@faire/deprecated-retailer-visitor-shared/components/LoggedOutPills/Pill";
import { LoadingSpinner } from "@faire/slate/components/loading-spinner";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import chunk from "lodash/chunk";
import React, { type JSX } from "react";
import styled from "styled-components";

export const StackedPillsWithTitle: React.FC<{
  pills: PillProps[];
  title?: string | JSX.Element;
  pillsCta?: JSX.Element;
  isLoading?: boolean;
  className?: string;
  useCarouselOnMobile?: boolean;
  leftAlign?: boolean;
  includeHairline?: boolean;
  mobileItemsPerSlide?: number;
  onView?: () => void;
}> = ({
  pills,
  title,
  pillsCta,
  isLoading,
  className,
  useCarouselOnMobile,
  leftAlign,
  includeHairline,
  mobileItemsPerSlide,
  onView,
}) => {
  const { isMobile } = useViewport();
  const viewRef = useDoActionWhenVisible(onView);
  return (
    <Wrapper className={className} ref={onView ? viewRef : undefined}>
      {title ? (
        <>
          {includeHairline ? (
            <>
              <HorizontalLine />
              <Spacer height="5x" />
            </>
          ) : null}
          <Header align={leftAlign || isMobile ? "left" : "center"}>
            {title}
          </Header>
          <Spacer height={{ mobileAndAbove: "2x", tabletAndAbove: "3x" }} />
        </>
      ) : null}
      {isLoading ? <LoadingSpinner size="medium" /> : null}
      {isMobile && useCarouselOnMobile ? (
        <StyledPillsMwebCarousel
          pills={pills}
          mobileItemsPerSlide={mobileItemsPerSlide}
        />
      ) : (
        <PillsContainer $leftAlign={leftAlign}>
          {pills.map((pill) => {
            return <Pill pill={pill} key={pill.pillContent} />;
          })}
          {pillsCta ? <CtaWrapper> {pillsCta} </CtaWrapper> : null}
        </PillsContainer>
      )}
    </Wrapper>
  );
};

const PillsMwebCarousel: React.FC<{
  pills: PillProps[];
  className?: string;
  mobileItemsPerSlide?: number;
}> = ({ pills, className, mobileItemsPerSlide }) => {
  const elementRef = React.useRef<HTMLDivElement>(null);
  return (
    <BaseFiniteScrollCarousel.BaseFiniteScrollCarousel
      forwardRef={elementRef}
      name="search-products-related-search-list-carousel"
      shouldHideRightButton
      shouldHideLeftButton
      className={className}
    >
      {mobileItemsPerSlide ? (
        <StyledColumn>
          {chunk(pills, mobileItemsPerSlide).map((pillsChunk, chunkIndex) => (
            <PillCarouselRow key={chunkIndex}>
              {pillsChunk.map((pill, pillIndex) => {
                const totalIndex = chunkIndex * mobileItemsPerSlide + pillIndex;
                return <Pill key={totalIndex} pill={pill} />;
              })}
            </PillCarouselRow>
          ))}
        </StyledColumn>
      ) : (
        <StyledRow>
          {pills.map((pill, pillIndex) => (
            <Pill key={pillIndex} pill={pill} />
          ))}
        </StyledRow>
      )}
    </BaseFiniteScrollCarousel.BaseFiniteScrollCarousel>
  );
};

const StyledRow = styled(Row).attrs({ gap: "1x" })`
  padding: ${getSpacing(0, "2x")};
  flex-wrap: wrap;
  min-width: 200vw;
`;

const StyledPillsMwebCarousel = styled(PillsMwebCarousel)`
  margin: ${getSpacing(0, "-2x")};
  width: unset;
`;

const StyledColumn = styled(Column)`
  padding: ${getSpacing(0, "2x")};
`;

const PillCarouselRow = styled(Row).attrs({ align: "flex-start", gap: "1x" })`
  &:not(:first-child) {
    margin-top: ${getSpacing("1x")};
  }
`;

const CtaWrapper = styled.div`
  padding-left: 4px;
  align-self: center;
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- move justify to a responsive prop
const PillsContainer = styled(Row).attrs({ gap: "1x" })<{
  $leftAlign?: boolean;
}>`
  width: 100%;

  flex-wrap: wrap;
  justify-content: ${({ $leftAlign: leftAlign }) =>
    leftAlign ? "left" : "center"};

  ${responsiveMediaQueries.mobile} {
    justify-content: left;
  }
`;

const Header = styled(Typography).attrs({
  variant: "pageHeaderSerifRegular",
  truncate: true,
  forwardedAs: "h4",
})`
  &&& {
    padding-left: 4px;
    word-break: break-word;
    white-space: normal;
    ${responsiveMediaQueries.tabletAndAbove} {
      padding-left: 0;
    }
  }
`;

const Wrapper = styled.div`
  padding-top: 0;
  width: 100%;
`;
